/* -------------------------------------------------------------------------
 * itemValidation.js — MUST stay in lockstep with the backend validator in
 * server.py `_validate_strict_item_payload`. Both sides iterate the SAME
 * shared JSON list at `src/shared/requiredItemFields.json` so the two
 * layers can never disagree — add / remove a field name there and both
 * validators update in one round-trip.
 *
 * Rules (2026-02 hardening — new writes only, existing items grandfathered):
 *   quantity  — required, numeric, integer, ≥ 0     (item_data["Total Bottles"])
 *   cost      — required, numeric, ≥ 0             (cost_price_ex_gst)
 *   category  — required, non-empty string         (category_id)
 *
 * Returns an object of field-key → error message. Empty object = valid.
 * ------------------------------------------------------------------------- */

import REQUIRED_ITEM_FIELDS from "@/shared/requiredItemFields.json";

export const STRICT_QTY_KEY = "Total Bottles";
export const STRICT_TAG_KEY = "Tag";
export { REQUIRED_ITEM_FIELDS };

const isBlank = (v) => v === null || v === undefined || (typeof v === "string" && v.trim() === "");
const asNumber = (v) => {
  if (v === "" || v === null || v === undefined) return NaN;
  const n = Number(v);
  return Number.isFinite(n) ? n : NaN;
};

export function validateItemPayload(payload = {}) {
  const errors = {};
  const itemData = payload.item_data && typeof payload.item_data === "object" ? payload.item_data : {};

  for (const field of REQUIRED_ITEM_FIELDS) {
    if (field === STRICT_QTY_KEY) {
      const raw = itemData[STRICT_QTY_KEY];
      if (isBlank(raw)) {
        errors[`item_data.${STRICT_QTY_KEY}`] = "Total Bottles is required";
        continue;
      }
      const q = asNumber(raw);
      if (Number.isNaN(q)) errors[`item_data.${STRICT_QTY_KEY}`] = "Total Bottles must be numeric";
      else if (q < 0) errors[`item_data.${STRICT_QTY_KEY}`] = "Total Bottles must be ≥ 0";
      else if (!Number.isInteger(q)) errors[`item_data.${STRICT_QTY_KEY}`] = "Total Bottles must be a whole number";
    } else if (field === "cost_price_ex_gst") {
      const raw = payload.cost_price_ex_gst;
      if (raw === null || raw === undefined || raw === "") {
        errors.cost_price_ex_gst = "Cost is required";
        continue;
      }
      const c = asNumber(raw);
      if (Number.isNaN(c)) errors.cost_price_ex_gst = "Cost must be numeric";
      else if (c < 0) errors.cost_price_ex_gst = "Cost must be ≥ 0";
    } else if (field === "category_id") {
      if (isBlank(payload.category_id)) errors.category_id = "Category is required";
    } else if (field === "Item Name") {
      // 2026-02-25 Unified Required Fields — Item Name is now a
      // save-gate for every collection. Lives inside `item_data`
      // (kept in lockstep with the shared JSON on the backend).
      const raw = itemData["Item Name"];
      if (isBlank(raw)) errors["item_data.Item Name"] = "Item Name is required";
    }
  }

  return errors;
}

/**
 * Given a backend 422 response of the shape `{ detail: { errors: { field: msg } } }`
 * (or the legacy `{ detail: "message" }`), normalise into the same shape our
 * frontend validator returns so consumers can render field-level errors from
 * either source with one code path.
 */
export function extractBackendErrors(err) {
  const detail = err?.response?.data?.detail;
  if (detail && typeof detail === "object" && detail.errors && typeof detail.errors === "object") {
    return { ...detail.errors };
  }
  if (typeof detail === "string") return { _global: detail };
  return { _global: err?.message || "Save failed" };
}
